import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import './App.css';
import './styles/redesign.css';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/Home').then(module => ({ default: module.HomePage })));
const GalleryPage = lazy(() => import('./pages/GalleryPage').then(module => ({ default: module.GalleryPage })));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const MenuFullPage = lazy(() => import('./pages/MenuFullPage'));
const BookingPage = lazy(() => import('./components/booking/BookingPage'));

// Wrapper component to set data-page attribute on body
const AppContent = () => {
  const location = useLocation();
  const [language, setLanguage] = useState<string & ('en' | 'pt')>(localStorage.getItem('language') as string & ('en' | 'pt') || 'en');
  const { t } = useTranslation();
  
  // Function to toggle language between English and Portuguese
  const toggleLanguage = () => {
    // Cycle through languages: en -> pt -> en
    const nextLanguage = {
      'en': 'pt',
      'pt': 'en'
    };
    
    const newLang = nextLanguage[language as keyof typeof nextLanguage] || 'en';
    setLanguage(newLang as string & ('en' | 'pt'));
    i18n.changeLanguage(newLang);
  };
  
  // Effect to sync language state with i18n
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);
  
  // Set the data-page attribute on the body element based on the current route
  useEffect(() => {
    const pageName = location.pathname.replace('/', '') || 'home';
    document.body.setAttribute('data-page', pageName);

    return () => {
      document.body.removeAttribute('data-page');
    };
  }, [location]);

  // Global scroll-reveal: reveal any .rc-reveal element as it enters the viewport.
  // Sections lazy-load progressively, so we re-scan the DOM a few times to pick
  // up late-mounted elements and observe any not-yet-tracked ones.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seen = new WeakSet<Element>();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('rc-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    const scan = () => {
      const els = Array.from(document.querySelectorAll('.rc-reveal')) as HTMLElement[];
      els.forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        if (reduce) { el.classList.add('rc-in'); return; }
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.92) el.classList.add('rc-in');
        else io.observe(el);
      });
    };

    scan();
    const timers = [120, 350, 700, 1200, 2000].map((ms) => window.setTimeout(scan, ms));
    // Guaranteed safety net: never leave content stuck hidden. After 1.8s,
    // force-reveal everything still observed (covers full-page screenshots,
    // slow devices, and any case the observer doesn't fire).
    const forceAll = window.setTimeout(() => {
      document.querySelectorAll('.rc-reveal:not(.rc-in)').forEach((el) => el.classList.add('rc-in'));
    }, 1000);
    return () => { io.disconnect(); timers.forEach(window.clearTimeout); window.clearTimeout(forceAll); };
  }, [location]);
  
  return (
      <div className="app">
        <Header 
          language={language} 
          toggleLanguage={toggleLanguage}
        />
        <main style={{ minHeight: '100vh' }}>
          <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
            <Routes>
              <Route path="/" element={<HomePage language={language} />} />
              <Route path="/gallery" element={<GalleryPage language={language} />} />
              <Route path="/menu" element={<Navigate to="/menu-full" replace />} />
              <Route path="/menu-full" element={<MenuFullPage language={language} />} />
              <Route path="/booking" element={<BookingPage language={language} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer language={language} />
      </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
